import { useEffect, useState, useCallback } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://platform.tahlil.one';

const COLORS = {
  analysisPrice: '#2C5F87',
  targetPrice: '#3D8B6E',
};

export function AnalysisPriceLineChart({ autoRefreshInterval = 60000 }) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include',
      });
      if (response.status === 401) {
        setAuthError(true);
        setAuthChecked(true);
        return false;
      }
      if (!response.ok) {
        setAuthError(true);
        setAuthChecked(true);
        return false;
      }
      const data = await response.json();
      if (data.authenticated) {
        setAuthError(false);
        setAuthChecked(true);
        return true;
      }
      setAuthError(true);
      setAuthChecked(true);
      return false;
    } catch (error) {
      setAuthError(true);
      setAuthChecked(true);
      return false;
    }
  }, []);

  const fetchChartData = useCallback(async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) {
      setIsRefreshing(true);
    }
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/daily-analysis/line-chart-data`, {
        credentials: 'include',
      });
      if (response.status === 401) {
        setAuthError(true);
        return;
      }
      if (!response.ok) {
        throw new Error('Failed to fetch chart data');
      }
      const data = await response.json();
      setChartData(data);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  const checkForUpdates = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/daily-analysis/last-sync`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setLastSync(data.last_sync);
      }
    } catch (error) {
      // Silent fail
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await checkAuth();
      if (isAuth) {
        await fetchChartData();
        await checkForUpdates();
      }
    };
    init();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      checkForUpdates();
    }, autoRefreshInterval);
    return () => clearInterval(interval);
  }, [autoRefreshInterval, checkForUpdates]);

  useEffect(() => {
    if (authError && authChecked) {
      navigate('/login');
    }
  }, [authError, authChecked, navigate]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border rounded p-2 shadow-lg">
          <p className="text-sm font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: ${entry.value?.toFixed(2)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading && !chartData.length) {
    return (
      <Card>
        <CardContent className="h-[400px] flex items-center justify-center">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>
            {language === 'ar' ? 'سعر التحليل مقابل السعر المستهدف' : 'Analysis Price vs Target Price'}
          </CardTitle>
          <CardDescription>
            {language === 'ar' ? 'مقارنة بين سعر التحليل والسعر المستهدف' : 'Comparison of analysis price and target price'}
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {lastSync && (
            <span className="text-xs text-muted-foreground">
              {language === 'ar' ? 'آخر تحديث:' : 'Last sync:'} {new Date(lastSync).toLocaleString()}
            </span>
          )}
          <button
            onClick={() => fetchChartData(true)}
            disabled={isRefreshing}
            className="p-2 rounded-md hover:bg-muted transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </CardHeader>
      <CardContent className="h-[400px]">
        {errorMessage ? (
          <div className="h-full flex items-center justify-center text-destructive">
            {errorMessage}
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            {language === 'ar' ? 'لا توجد بيانات متاحة' : 'No data available'}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="instrument_code" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="analysis_price"
                stroke={COLORS.analysisPrice}
                name="Analysis Price"
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="target_price"
                stroke={COLORS.targetPrice}
                name="Target Price"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
